import { CreateReportFormValues, queryOnceWasteMeasurementsForEvent, queryOnceReportTemplate, WasteMeasurement } from './schema';
import { convertAndFilterMeasurements } from './wasteConversion';

export const SOCIAL_REPORT_TEMPLATE_ID = 'e704d7d4-cdf6-4916-9f6a-4b009a221382';
export const SOCIAL_REPORT_NO_DIVERSION_TEMPLATE_ID = 'fb7b710c-31e5-4e4c-a71e-8b7862ec6363';
export const BREAKAWAY_REPORT_TEMPLATE_ID = 'b6700035-fd15-4cc2-be4e-fc60cac97765';
export const CLIENT_REPORT_TEMPLATE_ID = '54f89a1a-8866-4169-bcbc-fc7c95732f4d';
export const MATERIAL_BREAKDOWN_CHART_TEMPLATE_ID = 'df3810c7-73f6-436b-b0a8-65aa97456288';

// Define the raw data structure
interface EventData {
    wasteMeasurements: WasteMeasurement[];
}

// Define the chart data type
type ChartData = any[][];

// Define the derived data interface for all report types
interface ReportDerivedData {
    diversionRate: string;
    totalWeight: string;
    mtCo2eSaved: string;
    treesEquivalent: string;
    gasGallonsEquivalent: string;
    homesEquivalent: string;
    smartphonesEquivalent: string;
}

// Define chart data getters by chart template ID
export const CHART_DATA_GETTERS: Record<string, (data: EventData, params: CreateReportFormValues) => ChartData> = {
    [MATERIAL_BREAKDOWN_CHART_TEMPLATE_ID]: (data, params) => {
        const wasteMeasurements = data.wasteMeasurements || [];

        if (params.materialBreakdown) {
            // Group by material name and sum quantities
            const materialTotals: Record<string, number> = {};

            for (const measurement of wasteMeasurements) {
                const material = measurement.material;
                if (material?.name) {
                    if (!materialTotals[material.name]) {
                        materialTotals[material.name] = 0;
                    }
                    materialTotals[material.name] += measurement.quantity;
                }
            }

            // Convert to array format for the report
            const result: ChartData = [['Material', 'Quantity (lbs)']];

            Object.entries(materialTotals).forEach(([material, quantity]) => {
                result.push([material, quantity]);
            });

            return result;
        } else {
            // Group by category name and sum quantities
            const categoryTotals: Record<string, number> = {};

            for (const measurement of wasteMeasurements) {
                const category = measurement.material?.category?.name;
                if (category) {
                    if (!categoryTotals[category]) {
                        categoryTotals[category] = 0;
                    }
                    categoryTotals[category] += measurement.quantity;
                }
            }

            // Convert to array format for the report
            const result: ChartData = [['Category', 'Quantity (lbs)']];

            Object.entries(categoryTotals).forEach(([category, quantity]) => {
                result.push([category, quantity]);
            });

            return result;
        }
    },
};

// CO2 equivalency conversion factors
const CO2_EQUIVALENCY_FACTORS = {
    TREES: 0.06, // Metric tons CO2 per tree
    GAS_GALLONS: 0.008887, // Metric tons CO2 per gallon of gasoline
    HOME_ELECTRIC: 5.139, // Metric tons CO2 per home's electricity use
    SMARTPHONES_CHARGED: 0.00000822, // Metric tons CO2 per smartphone charged
};

/**
 * Transform raw event data into derived metrics for all report types.
 * This single transformer is used by all report templates.
 */
function transformReportData(data: EventData, _params: CreateReportFormValues): ReportDerivedData {
    const total = data.wasteMeasurements.reduce((acc, m) => acc + m.quantity, 0);
    const landfill = data.wasteMeasurements.filter(m => m.material?.category?.name === 'Landfill').reduce((acc, m) => acc + m.quantity, 0);
    const diversionRate = total > 0 ? (1 - landfill / total) * 100 : 0;

    // Calculate CO2 savings
    const mtCo2eSaved = calculateCo2Savings(data.wasteMeasurements);

    // Calculate equivalencies
    const equivalencies = calculateCo2Equivalencies(mtCo2eSaved);

    return {
        diversionRate: diversionRate.toFixed(1),
        totalWeight: total.toFixed(1),
        mtCo2eSaved: mtCo2eSaved.toFixed(2),
        treesEquivalent: equivalencies.trees.toFixed(0),
        gasGallonsEquivalent: equivalencies.gasGallons.toFixed(0),
        homesEquivalent: equivalencies.homes.toFixed(2),
        smartphonesEquivalent: equivalencies.smartphones.toFixed(0),
    };
}

/**
 * Calculate total CO2 savings from waste measurements
 */
function calculateCo2Savings(measurements: WasteMeasurement[]): number {
    let totalMtCo2eSaved = 0;

    for (const measurement of measurements) {
        const material = measurement.material;
        if (material?.mtCo2eSavedPerTon) {
            // Convert pounds to tons (1 ton = 2000 lbs) and multiply by CO2 savings per ton
            const tons = measurement.quantity / 2000;
            totalMtCo2eSaved += tons * material.mtCo2eSavedPerTon;
        }
    }

    return totalMtCo2eSaved;
}

/**
 * Calculate CO2 equivalencies based on total CO2 savings
 */
function calculateCo2Equivalencies(mtCo2eSaved: number) {
    return {
        trees: mtCo2eSaved / CO2_EQUIVALENCY_FACTORS.TREES,
        gasGallons: mtCo2eSaved / CO2_EQUIVALENCY_FACTORS.GAS_GALLONS,
        homes: mtCo2eSaved / CO2_EQUIVALENCY_FACTORS.HOME_ELECTRIC,
        smartphones: mtCo2eSaved / CO2_EQUIVALENCY_FACTORS.SMARTPHONES_CHARGED,
    };
}

// Add this mapping for friendly display names
export const DERIVED_DATA_DISPLAY_NAMES: Record<string, string> = {
    diversionRate: 'Diversion Rate (%)',
    totalWeight: 'Total Weight (lbs)',
    mtCo2eSaved: 'MTCO2E Saved',
    treesEquivalent: 'Trees',
    gasGallonsEquivalent: 'Gallons of Gas',
    homesEquivalent: 'Homes Powered',
    smartphonesEquivalent: 'Smartphones Charged',
};

// Shared placeholders for derived data - all report templates use the same placeholders
// Templates can choose which placeholders to include in their slides
export const REPORT_DERIVED_DATA_PLACEHOLDERS: Record<string, string> = {
    diversionRate: '{{DR}}',
    totalWeight: '{{TW}}',
    mtCo2eSaved: '{{CO2}}',
    treesEquivalent: '{{TREES}}',
    gasGallonsEquivalent: '{{GAS}}',
    homesEquivalent: '{{HOMES}}',
    smartphonesEquivalent: '{{SP}}',
};

export interface ReportDataResult {
    chartData: Record<string, ChartData>;
    derivedData: ReportDerivedData;
}

export const getReportData = async (templateId: string, eventId: string, params: CreateReportFormValues): Promise<ReportDataResult> => {
    // Fetch raw data once
    const { data } = await queryOnceWasteMeasurementsForEvent(eventId);

    // Convert and filter measurements to pounds
    const convertedMeasurements = convertAndFilterMeasurements(data?.wasteMeasurements || [], {
        materialBreakdown: params.materialBreakdown,
        itemIds: params.materialBreakdown ? params.materialIds : params.categoryIds,
        datasetIds: params.datasetIds,
        targetUnit: 'lb',
    });

    // Convert to our well-typed EventData structure
    const eventData: EventData = {
        wasteMeasurements: convertedMeasurements,
    };

    // Fetch report template with its chart templates
    const { data: templateData } = await queryOnceReportTemplate(templateId);

    const reportTemplate = templateData?.reportTemplates[0];
    if (!reportTemplate) {
        throw new Error(`Report template not found for id: ${templateId}`);
    }

    const chartTemplateIds = reportTemplate.chartTemplates.map(ct => ct.id);
    if (chartTemplateIds.length === 0) {
        throw new Error(`No chart templates found for report templateId: ${templateId}`);
    }

    // Apply shared transformation to raw data (same for all report types)
    const derivedData = transformReportData(eventData, params);

    // Collect data for each chart template
    const chartData: Record<string, ChartData> = {};

    for (const chartTemplateId of chartTemplateIds) {
        const chartDataGetter = CHART_DATA_GETTERS[chartTemplateId];
        if (!chartDataGetter) {
            throw new Error(`No chart data getter found for chartTemplateId: ${chartTemplateId}`);
        }

        chartData[chartTemplateId] = chartDataGetter(eventData, params);
    }

    return {
        chartData,
        derivedData,
    };
};
